import { ApplicationView, useController } from '@aha-app/mvc';
import classNames from 'classnames';
import React from 'react';
import { z } from 'zod';
import customElementBoolAttr from '../../../utils/customElementBoolAttr';
import { useDebounce } from '../../ValidatedTextInput';
import VotingSessionController, {
  type PokerPreset,
} from './controllers/VotingSessionController';
import { PokerCard } from './PokerCard';
import styles from './VotingSession.module.css';
import { pokerValuesFieldData } from './VotingSetupForm';

const pokerPresetNames: Record<Exclude<PokerPreset, undefined>, string> = {
  custom: 'Custom',
  fibonacci: 'Fibonacci',
  tshirt: 'T-shirt size',
} as const;

const pokerPresets: PokerPreset[] = ['fibonacci', 'tshirt', 'custom'];

const zValidateCustomValues = z.string().transform((value, ctx) => {
  // Handle completely empty input case
  if (value.trim() === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'List at least two values separated by commas.',
    });
    return z.NEVER;
  }

  const rawValues = value.trim().split(',');
  const validValues = rawValues.filter(v => v.trim().length > 0);
  const hasTrailingComma = value.trim().endsWith(',');

  // Special case for exactly one value with a trailing comma like "1,"
  if (validValues.length === 1 && hasTrailingComma && rawValues.length === 2) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'At least two values are required.',
    });
    return z.NEVER;
  }

  // Check for empty values, but ignore trailing comma when there are at least 2 valid values
  const hasEmptyValues = rawValues.some((v, i) => {
    // Don't count the last empty element if it's from a trailing comma and we have >= 2 valid values
    if (
      i === rawValues.length - 1 &&
      hasTrailingComma &&
      validValues.length >= 2
    ) {
      return false;
    }
    return v.trim() === '';
  });

  if (hasEmptyValues) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Empty values are not allowed.',
    });
    return z.NEVER;
  }

  // Check if there's less than two valid values
  if (validValues.length < 2) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'At least 2 values are required.',
    });
    return z.NEVER;
  }

  const uniques = new Set(validValues);
  if (uniques.size !== validValues.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Remove duplicate values.',
    });
    return z.NEVER;
  }

  return validValues;
});

export const VotingSetupPokerOptions = ApplicationView(
  ({
    customValuesField,
    setCustomValuesField,
  }: {
    customValuesField: {
      value: string;
      error: string | null;
    };
    setCustomValuesField: ({
      value,
      error,
    }: {
      value: string;
      error: string | null;
    }) => void;
  }) => {
    const controller = useController(VotingSessionController);
    const pokerPreset = controller.state.pokerPreset;

    const debouncedErrorMessage = useDebounce(customValuesField.error, 1000);

    /** TODO: Merge with `ValidatedTextInput`.
     * This is very similar to (and actually the original version of)
     * `ValidatedTextInput` and it would be nice if this used ValidatedTextInput too.
     * It's definitely possible with some changes to ValidatedTextInput.
     * I took a quick swing at it but needed to shelve it to move on with my ticket.
     */
    const inputValidator = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const result = zValidateCustomValues.safeParse(value);
      if (!result.success) {
        setCustomValuesField({
          value,
          error: result.error.issues[0].message,
        });
      } else {
        setCustomValuesField({ value, error: null });
      }
      if (value !== pokerValuesFieldData[pokerPreset]) {
        controller.actionSetPokerPreset('custom');
      }
    };

    const pokerPresetName = pokerPresetNames[pokerPreset];
    return (
      <>
        <div className={styles.optionToggleInput}>
          <p className='m-0 color-secondary'>Estimation units</p>
          <aha-menu>
            <span
              slot='control'
              className={classNames('t-150', styles.menuTrigger)}
            >
              {pokerPresetName}
              <aha-icon class='ml-2' icon='fa-solid fa-chevron-down' />
            </span>
            <aha-menu-content>
              <aha-menu-group>
                {pokerPresets.map(preset => {
                  return (
                    <aha-menu-item
                      key={preset}
                      selected={customElementBoolAttr(pokerPreset === preset)}
                      onClick={() => {
                        controller.actionSetPokerPreset(preset);
                        setCustomValuesField({
                          value: pokerValuesFieldData[preset],
                          error: null,
                        });
                      }}
                    >
                      {pokerPresetNames[preset]}
                    </aha-menu-item>
                  );
                })}
              </aha-menu-group>
            </aha-menu-content>
          </aha-menu>
        </div>
        <div
          className={classNames(
            styles.optionToggleInput,
            styles.pokerCustomValuesField,
            debouncedErrorMessage && styles.pokerCustomValuesError
          )}
        >
          <p className='m-0 color-secondary gap-2'>Enter custom units</p>
          <input
            className={styles.pokerCustomValuesInput}
            type='text'
            autoComplete='off'
            name='custom'
            disabled={false}
            onChange={inputValidator}
            value={customValuesField.value}
          />
          {debouncedErrorMessage && (
            <aha-alert type='danger' style={{ width: '100%' }}>
              {debouncedErrorMessage}
            </aha-alert>
          )}
        </div>
        <div className={styles.pokerCardList}>
          {customValuesField.value
            .split(',')
            .map(value => value.trim())
            .filter(value => value.length > 0)
            .map((value, index) => {
              return (
                <PokerCard
                  cardColor='normal'
                  cardSize='normal'
                  value={value}
                  key={`${value}-${index}`} // Can't just use `value` because there might be duplicates
                />
              );
            })}
        </div>
      </>
    );
  }
);
